import React, {
  useState, useEffect, useCallback,
  forwardRef, useImperativeHandle,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckSquare, Pill, Smile, Heart, Calendar, Loader2, Check,
} from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const ACTION_CONFIG = {
  medication: {
    Icon: Pill,
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    ctaLabel: 'Take it',
    ctaCls: 'bg-indigo-500 hover:bg-indigo-600',
  },
  mood: {
    Icon: Smile,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
    ctaLabel: 'Check in',
    ctaCls: 'bg-pink-600 hover:bg-pink-700',
  },
  vitals: {
    Icon: Heart,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    ctaLabel: 'Log',
    ctaCls: 'bg-amber-500 hover:bg-amber-600',
  },
  appointment: {
    Icon: Calendar,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    ctaLabel: 'View',
    ctaCls: 'bg-blue-600 hover:bg-blue-700',
  },
};

/**
 * DailyActionsWidget
 *
 * Props:
 *   onMoodCheckin  — called when patient taps "Check in"; should scroll to mood section
 *   onVitalsLog    — called when patient taps "Log"; should expand the vitals widget
 *
 * Ref methods (via useImperativeHandle):
 *   markDone(type) — removes the action of the given type and fires caregiver notify.
 *                    Called by PatientDashboard after mood / vitals complete.
 */
const DailyActionsWidget = forwardRef(function DailyActionsWidget(
  { onMoodCheckin, onVitalsLog },
  ref
) {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [doneCount, setDoneCount] = useState(0);
  const [completing, setCompleting] = useState(null); // action.id being processed

  const fetchActions = useCallback(async () => {
    try {
      const res = await api.get('/daily-actions/today');
      setActions(res.data?.data || []);
    } catch {
      // silent — dashboard remains usable without this widget
    }
  }, []);

  useEffect(() => {
    fetchActions();
    const onFocus = () => fetchActions();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchActions]);

  const notifyCaregiver = (type, label) => {
    api.post('/daily-actions/notify-caregiver', { type, label }).catch(() => {});
  };

  const removeAction = useCallback((id) => {
    setActions(prev => prev.filter(a => a.id !== id));
    setDoneCount(d => d + 1);
  }, []);

  // Exposed to PatientDashboard via ref
  useImperativeHandle(ref, () => ({
    markDone: (type) => {
      setActions(prev => {
        const action = prev.find(a => a.type === type);
        if (action) {
          notifyCaregiver(type, action.label);
          setDoneCount(d => d + 1);
          return prev.filter(a => a.type !== type);
        }
        return prev;
      });
    },
  }), []);

  const handleCta = async (action) => {
    if (completing) return;
    setCompleting(action.id);
    try {
      if (action.type === 'medication') {
        const today = new Date().toISOString().slice(0, 10);
        await api.post(`/patient-profile/me/medications/${action.id}/log`, {
          taken: true,
          date: today,
        });
        removeAction(action.id);
        notifyCaregiver('medication', action.label);
      } else if (action.type === 'mood') {
        onMoodCheckin?.();
        // Removal handled by PatientDashboard calling ref.markDone('mood')
      } else if (action.type === 'vitals') {
        onVitalsLog?.();
        // Removal handled by PatientDashboard calling ref.markDone('vitals')
      } else if (action.type === 'appointment') {
        navigate(`/care-events/${action.id}`);
      }
    } catch {
      toast.error('Could not complete action. Please try again.');
    } finally {
      setCompleting(null);
    }
  };

  // Don't render until we know what actions exist
  if (actions.length === 0 && doneCount === 0) return null;

  const totalCount = doneCount + actions.length;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
  const allDone = actions.length === 0 && doneCount > 0;

  if (allDone) {
    return (
      <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-4">
        <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Check className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-extrabold text-sm text-emerald-800">All done for today</p>
          <p className="text-xs text-emerald-500">{totalCount} of {totalCount} actions completed</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-indigo-500" />
          <span className="font-extrabold text-sm text-slate-900">Today's Actions</span>
        </div>
        <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">
          {doneCount} of {totalCount} done
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 bg-slate-100">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Action rows */}
      <div className="p-3 flex flex-col gap-2">
        {actions.map(action => {
          const cfg = ACTION_CONFIG[action.type];
          if (!cfg) return null;
          const { Icon } = cfg;
          const isCompleting = completing === action.id;
          return (
            <div
              key={action.id}
              className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-100"
            >
              <div
                className={`w-8 h-8 rounded-lg ${cfg.iconBg} flex items-center justify-center flex-shrink-0`}
              >
                <Icon className={`w-4 h-4 ${cfg.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{action.label}</p>
                {action.detail && (
                  <p className="text-xs text-slate-400">{action.detail}</p>
                )}
              </div>
              <button
                onClick={() => handleCta(action)}
                disabled={isCompleting || !!completing}
                className={`w-16 flex-shrink-0 py-1.5 rounded-lg text-xs font-bold text-white transition-colors disabled:opacity-50 ${cfg.ctaCls}`}
              >
                {isCompleting
                  ? <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                  : cfg.ctaLabel}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default DailyActionsWidget;
